import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { validateInput } from '../../util/validateFormInput';
import useScrollPosition from '../../../hooks/useScrollPosition';
import { fetchFormDataStart, submitFormRequest } from '../../../redux/slices/subSellerSignUpSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const SignUp = () => {
  const theme = useTheme();
  const scrollPosition = useScrollPosition();

  const isMobile = useMediaQuery(theme.breakpoints.between('xs', 'md'));

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const { businessModelOptions, GDSOptions, formSubmitted } = useSelector((state: any) => state.subSellerSignUp);

  const [formData, setFormData] = React.useState({
    seller_aggregator_id: '',
    travel_agency_name: '',
    iata_number: '',
    agency_address: '',
    agency_phone_number: '',
    agency_email: '',
    agency_website: '',
    sub_seller_business_type_id: '',
    contact_first_name: '',
    contact_last_name: '',
  });

  const [formValidation, setFormValidation] = React.useState({
    seller_aggregator_id: false,
    travel_agency_name: false,
    iata_number: false,
    agency_address: false,
    agency_phone_number: false,
    agency_email: false,
    agency_website: false,
    sub_seller_business_type_id: false,
    contact_first_name: false,
    contact_last_name: false,
  });

  const [formError, setFormError] = React.useState({
    seller_aggregator_id: false,
    travel_agency_name: false,
    iata_number: false,
    agency_address: false,
    agency_phone_number: false,
    agency_email: false,
    agency_website: false,
    sub_seller_business_type_id: false,
    contact_first_name: false,
    contact_last_name: false,
  });

  React.useEffect(() => {
    dispatch(fetchFormDataStart());
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  }, [dispatch]);

  const [showSelect, setShowSelect] = React.useState(false);
  const [showBusinessType, setShowBusinessType] = React.useState(false);

  React.useEffect(() => {
    if (scrollPosition) {
      setShowSelect(false)
      setShowBusinessType(false)
    }
  }, [scrollPosition]);

  const isValidForm = !Object.values(formValidation).every((item) => item);

  const handleFormData = (e: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value
    }));
    const op = validateInput(
      {
        content: e.target.value,
        validate: e.target.id
      },
      t
    );
    setFormError((prevData) => ({
      ...prevData,
      [e.target.name]: op.msg
    }));
    setFormValidation((prevData) => ({
      ...prevData,
      [e.target.name]: !op.error
    }));
  };

  const handleSubmit = async () => {
    dispatch(submitFormRequest(formData));
  };

  const gdsOptions = GDSOptions[i18n.language].slice().sort((a, b)=>a.company_name.localeCompare(b.company_name));
  
  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  // if (error) {
  //   return <div>Error: {error}</div>;
  // }

  return (
    <Box sx={{ display: 'flex', mt: '1rem' }}>
      <main style={{ width: '100%' }}>
      {formSubmitted ? (
        <Box
          sx={{
            bgcolor: '#f1f7fa',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '86vh',
            width: '100%'
          }}
        >
          <Paper
            elevation={3}
            sx={{
              py: '2rem',
              px: isMobile ? '1rem' : '5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '40rem',
              mx: isMobile ? '1rem' : 0
            }}
          >
            <Typography
              variant="h5"
              fontWeight={600}
              mb={3}
              textAlign={'center'}
            >
              {t('subSellerOnboarding.conclude.title')}
            </Typography>
            <Typography fontSize={'.875rem'}>
              {t('subSellerOnboarding.conclude.subtitle.0')}
            </Typography>

            <Typography fontSize={'.875rem'} textAlign={'center'}>
              {t('subSellerOnboarding.conclude.subtitle.1')}
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Grid container columnGap={7}>
          <Grid item xs={12} sm={12} md={4} lg={4} bgcolor="#f1f7fa" p={4}>
            <Typography variant="h1" mb={2} fontWeight={600} fontSize="1.5rem" lineHeight={1.334} letterSpacing="0em">
              {t('subSellerOnboarding.sidebar.title')}
            </Typography>
            <Typography
              fontSize={'.875rem'}
              mb={2}
              fontWeight={600}
              color="#d8292f"
            >
              {t('subSellerOnboarding.sidebar.note')}
            </Typography>
            <Typography fontSize={'.875rem'} mb={2}>
              {t('subSellerOnboarding.sidebar.subtitle.0')}
            </Typography>
            <Typography fontSize={'.875rem'}>
              {t('subSellerOnboarding.sidebar.subtitle.1')}
            </Typography>
          </Grid>
          <Grid
            item
            container
            xs={12}
            sm={12}
            md={7}
            lg={7}
            pt={4}
            sx={{ ...(isMobile && { mx: 4 }) }}
          >
            <Grid item xs={12} sm={12} md={6} lg={6} mb={isMobile ? 5 : 27}>
              <Typography fontWeight={600} fontSize={'20px'} gutterBottom>
                {t('subSellerOnboarding.form.title')}
              </Typography>
              <FormControl variant="standard"
                fullWidth sx={{ mt: 2 }}
                error={Boolean(formError.seller_aggregator_id)}
              >
                <InputLabel
                  id="seller_aggregator_id"
                  required
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: '1.25rem',
                    color: '#C1C5C8'
                  }}
                >
                  {t('subSellerOnboarding.form.field1.data')}
                </InputLabel>
                <Select
                  labelId="seller_aggregator_id"
                  required
                  id="text"
                  name="seller_aggregator_id"
                  value={formData.seller_aggregator_id}
                  onChange={handleFormData}
                  sx={{
                    fontSize: '.9rem',
                    fontWeight: 400,
                    lineHeight: '1.25rem',
                    color: '#000',
                    py: '.2rem'
                  }}
                  inputProps={{
                    style: {
                      borderBottom: `1px solid #C1C5C8`
                    },
                    'aria-label': `${t('subSellerOnboarding.form.ariaLabel')}`
                  }}
                  open={showSelect}
                  onOpen={() => setShowSelect(true)}
                  onClose={() => setShowSelect(false)}
                  MenuProps={{}}
                >
                  {gdsOptions.map((item) => (
                    <MenuItem
                      key={item.seller_aggregator_id}
                      value={item.seller_aggregator_id}
                    >
                      {item.company_name}
                    </MenuItem>
                  ))}
                </Select>
                {formError.seller_aggregator_id && <FormHelperText>{formError.seller_aggregator_id}</FormHelperText>}
              </FormControl>

              <TextField
                variant="standard"
                id="text"
                name="travel_agency_name"
                value={formData.travel_agency_name}
                onChange={handleFormData}
                error={Boolean(formError.travel_agency_name)}
                helperText={formError.travel_agency_name}
                InputLabelProps={{
                  // htmlFor:"travel_agency_name",
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field2.data')}
                inputProps={{
                  'aria-label':t('subSellerOnboarding.form.field2.data'),
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />

              <TextField
                variant="standard"
                id="iata"
                name="iata_number"
                value={formData.iata_number}
                onChange={handleFormData}
                error={Boolean(formError.iata_number)}
                helperText={
                  formError.iata_number || t('subSellerOnboarding.form.field3.helperText')
                }
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field3.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />

              <TextField
                variant="standard"
                id="text"
                name="agency_address"
                value={formData.agency_address}
                onChange={handleFormData}
                error={Boolean(formError.agency_address)}
                helperText={formError.agency_address}
                InputLabelProps={{
                  // htmlFor: "agency_address",
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field4.data')}
                inputProps={{
                  'aria-label':t('subSellerOnboarding.form.field4.data'),
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />

              <TextField
                variant="standard"
                id="phone"
                name="agency_phone_number"
                value={formData.agency_phone_number}
                onChange={handleFormData}
                error={Boolean(formError.agency_phone_number)}
                helperText={formError.agency_phone_number}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field5.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />

              <TextField
                variant="standard"
                id="text"
                name="contact_first_name"
                value={formData.contact_first_name}
                onChange={handleFormData}
                error={Boolean(formError.contact_first_name)}
                helperText={formError.contact_first_name}
                InputLabelProps={{
                  // htmlFor: "contact_first_name",
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field10.data')}
                inputProps={{
                  'aria-label': t('subSellerOnboarding.form.field10.data'),
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />

              <TextField
                variant="standard"
                id="text"
                name="contact_last_name"
                value={formData.contact_last_name}
                onChange={handleFormData}
                error={Boolean(formError.contact_last_name)}
                helperText={formError.contact_last_name}
                InputLabelProps={{
                  // htmlFor: "contact_last_name",
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field11.data')}
                inputProps={{
                  'aria-label': t('subSellerOnboarding.form.field11.data'),
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="email"
                name="agency_email"
                value={formData.agency_email}
                onChange={handleFormData}
                error={Boolean(formError.agency_email)}
                helperText={
                  formError.agency_email || t('subSellerOnboarding.form.field6.helperText')
                }
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field6.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="url"
                name="agency_website"
                value={formData.agency_website}
                onChange={handleFormData}
                error={Boolean(formError.agency_website)}
                helperText={formError.agency_website}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('subSellerOnboarding.form.field7.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <Typography
                fontWeight={600}
                fontSize='20px'
                mt={5}
                gutterBottom
              >
                {t('subSellerOnboarding.form.field9.data')}
              </Typography>

              <FormControl variant="standard" fullWidth sx={{ mt: 2 }}
                error={Boolean(formError.sub_seller_business_type_id)}
              >
                <InputLabel
                  id="sub_seller_business_type_id"
                  required
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: '1.25rem',
                    color: '#C1C5C8'
                  }}
                >
                  {t('subSellerOnboarding.form.field9.data')}
                </InputLabel>
                <Select
                  labelId="sub_seller_business_type_id"
                  required
                  id="text"
                  name="sub_seller_business_type_id"
                  // error={formError.sub_seller_business_type_id}
                  value={formData.sub_seller_business_type_id}
                  onChange={handleFormData}
                  sx={{
                    fontSize: '.9rem',
                    fontWeight: 400,
                    lineHeight: '1.25rem',
                    color: '#000',
                    py: '.2rem'
                  }}
                  inputProps={{
                    style: {
                      borderBottom: `1px solid #C1C5C8`
                    }
                  }}
                  open={showBusinessType}
                  onOpen={() => setShowBusinessType(true)}
                  onClose={() => setShowBusinessType(false)}
                  MenuProps={{
                    disableScrollLock: true
                  }}
                >
                  {businessModelOptions[i18n.language].map((item) => (
                    <MenuItem
                      key={item.sub_seller_business_type_id}
                      value={item.sub_seller_business_type_id}
                    // style={getStyles(name, personName, theme)}
                    >
                      {item.business_type}
                    </MenuItem>
                  ))}
                </Select>
                {formError.sub_seller_business_type_id && <FormHelperText>{formError.sub_seller_business_type_id}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid
              item
              container
              xs={12}
              sm={12}
              md={6}
              lg={6}
              mb={isMobile ? 7 : 15}
              alignItems={'end'}
              justifyContent={'center'}
            >
              <Button
                onClick={handleSubmit}
                sx={{
                  borderRadius: 3,
                  height: 'fit-content',
                  width: isMobile ? '100%  ' : '13rem',
                  bgcolor: '#005078',
                  color: theme.palette.primary.contrastText,
                  '&:hover': { backgroundColor: '#005078' },
                  boxShadow: '0 0 1rem #ccc',
                  textTransform: 'none',
                  fontSize: '1rem',
                  ...(isValidForm && {
                    bgcolor: '#D0D0CE',
                    color: '#737373',
                    boxShadow: 'none'
                  })
                }}
                disabled={isValidForm}
              >
                {t('subSellerOnboarding.form.submit')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      </main>
    </Box>
  );
};

export default SignUp;
